import { motion } from 'motion/react';
import { createContext, ReactNode, useContext } from 'react';
import { stageTransition } from 'v5/primitives/AnimatedHeight';
import { cn } from 'v5/platform/dom/cn';

type StagesContext = {
  stage: string;
  order: readonly string[];
};

const stagesContext = createContext<StagesContext | null>(null);

export interface AnimatedStagesProps<Stage extends string> {
  /** The stage on screen. */
  stage: Stage;
  /** Every stage in flow order — a later stage waits on the right, an earlier one on the left. */
  order: readonly Stage[];
  /** `AnimatedStage` blocks, one per stage. */
  children: ReactNode;
  className?: string;
}

/**
 * Switches between blocks of one flow with a slide, keeping every block mounted — so a form split
 * over steps keeps what was typed when the user goes back. Only the active block takes space; wrap
 * the scrolling area or the dialog in `AnimatedHeight` to ease the height as well.
 *
 * @example
 * ```tsx
 * <AnimatedStages stage={step} order={['setup', 'form']}>
 *   <AnimatedStage stage="setup">…</AnimatedStage>
 *   <AnimatedStage stage="form">…</AnimatedStage>
 * </AnimatedStages>
 * ```
 */
export function AnimatedStages<Stage extends string>({
  stage,
  order,
  children,
  className
}: AnimatedStagesProps<Stage>) {
  return (
    <stagesContext.Provider value={{ stage, order }}>
      <div className={className}>
        {/* clip, not hidden: no scroll container, so sticky parts inside keep sticking */}
        <div className="overflow-x-clip">
          <div className="relative">{children}</div>
        </div>
      </div>
    </stagesContext.Provider>
  );
}

export interface AnimatedStageProps {
  stage: string;
  children: ReactNode;
  className?: string;
}

export function AnimatedStage({
  stage,
  children,
  className
}: AnimatedStageProps) {
  const context = useContext(stagesContext);
  if (!context) {
    throw new Error('AnimatedStage must be rendered inside AnimatedStages');
  }
  const isActive = context.stage === stage;
  const side = Math.sign(
    context.order.indexOf(stage) - context.order.indexOf(context.stage)
  );

  const hidden = { opacity: 0, x: `${side * 20}rem` };
  // Out of the tab order and the accessibility tree while it slides away; React 18 has no
  // `inert` prop, so the attribute goes through as a plain string.
  const inert = isActive ? {} : { inert: '' };

  return (
    <motion.div
      {...inert}
      aria-hidden={!isActive}
      className={cn(
        // a leaving block is taken out of flow at once, so the height follows the new one, and cut
        // to the new height, so a taller one doesn't stretch the scroll area while it fades out
        !isActive && 'pointer-events-none absolute inset-0 overflow-hidden',
        className
      )}
      // a block that starts hidden starts gone, not only transparent
      initial={isActive ? false : { ...hidden, display: 'none' }}
      animate={
        isActive
          ? { opacity: 1, x: 0, display: 'block' }
          : {
              ...hidden,
              // gone once it's out of sight — a hidden block must not stretch the scroll area
              transitionEnd: { display: 'none' }
            }
      }
      transition={stageTransition}
    >
      {children}
    </motion.div>
  );
}
